import React from "react";
import {
  Avatar,
  Button,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import CatalogedProduct from "shared-ui/src/types/ecommerce/CatalogedProduct";
import NoProductImage from "@images/no-product-image-available.svg";
import { PRODUCT_IMAGE_SIZE_MEDIUM } from "shared-ui/src/types/ecommerce/constants";
import { getShiComBaseUrl } from "@lib/shiComBaseUrl";
import CheckIcon from "@mui/icons-material/Check";
import { goodToBadColors } from "shared-ui";
import { alpha } from "@mui/material/styles";
import { ShoppingCart } from "@mui/icons-material";
import { getFormattedPrice } from "shared-ui/src/components/ecommerce/helpers/getValidPrice";

const getInventoryColor = (
  inventory: string | undefined,
): string | undefined => {
  if (!inventory) {
    return undefined;
  }
  if (inventory === "Backorder") {
    return goodToBadColors.bad;
  } else if (inventory === "Verify Stock") {
    return goodToBadColors.warning;
  } else {
    return "text.secondary";
  }
};

export type ProductListItem = CatalogedProduct;

export interface ProductListProps {
  products?: ProductListItem[];
  onAddToCart?: (product: ProductListItem) => void;
  formatPrice?: (amount: number) => string;
}

export default function ProductList({
  products,
  onAddToCart,
  formatPrice,
}: Readonly<ProductListProps>) {
  const shiComBaseUrl = getShiComBaseUrl();

  if (!products?.length) {
    return null;
  }

  // Calculate approximate height for 5 list items
  // Each ListItem is approximately 72px (avatar + padding)
  const maxVisibleItems = 5;
  const listItemHeight = PRODUCT_IMAGE_SIZE_MEDIUM + 16 * 2;
  const maxHeight = maxVisibleItems * listItemHeight;

  return (
    <List
      disablePadding
      aria-label="Featured products list"
      sx={{
        maxHeight: products.length > maxVisibleItems ? maxHeight : "auto",
        overflowY: products.length > maxVisibleItems ? "auto" : "visible",
      }}
    >
      {products.map((p) => {
        const price = getFormattedPrice(p.price, formatPrice);

        const productUrl = `${shiComBaseUrl}/product/${p.productDescriptor}`;

        const inventory =
          p.availability?.includes("Unknown") === false
            ? p.availability
            : undefined;
        const inventoryColor = getInventoryColor(inventory);

        const canAddToCart =
          onAddToCart !== undefined && p.canAddToCart === true;

        // SYN-676: Backorder/Verify Stock items are orderable (consistent with the Standards
        // Shop grid and cart recommendations). Only block adds when availability is genuinely unknown.
        const disabledAddToCart = inventory === undefined || !canAddToCart;

        const addToCartAriaLabel = disabledAddToCart
          ? `Add ${p.name || "product"} to cart - Call for price and availability`
          : `Add ${p.name || "product"} to cart`;

        return (
          <ListItem
            key={p.id}
            divider
            role="article"
            aria-label={`Product: ${p.name || "Unnamed product"}`}
            secondaryAction={
              <Stack direction="row" alignItems="center" spacing={1}>
                <Stack
                  sx={{ minWidth: 100, textAlign: "right" }}
                  aria-label="Product pricing and availability"
                >
                  <Typography
                    variant="body2"
                    color="text.primary"
                    aria-label={`Price: ${price}`}
                  >
                    {price}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    {inventory === "In Stock" && (
                      <CheckIcon
                        color="success"
                        fontSize="small"
                        aria-hidden="true"
                      />
                    )}
                    <Typography
                      variant="caption"
                      color={inventoryColor}
                      aria-label={`Availability: ${inventory || "Unknown"}`}
                    >
                      {inventory}
                    </Typography>
                  </Stack>
                </Stack>
                {canAddToCart && (
                  <Button
                    aria-label={addToCartAriaLabel}
                    variant="text"
                    size="small"
                    disabled={disabledAddToCart}
                    onClick={() => onAddToCart?.(p)}
                    startIcon={<ShoppingCart aria-hidden="true" />}
                  >
                    Add
                  </Button>
                )}
              </Stack>
            }
          >
            <ListItemAvatar>
              <Avatar
                variant="square"
                alt={
                  p.imageUrl
                    ? `${p.name || "Product"} image`
                    : "No product image available"
                }
                src={p.imageUrl ?? NoProductImage}
                sx={{
                  width: PRODUCT_IMAGE_SIZE_MEDIUM,
                  height: PRODUCT_IMAGE_SIZE_MEDIUM,
                  "& img": {
                    objectFit: "contain",
                    objectPosition: "center",
                  },
                }}
              />
            </ListItemAvatar>
            <ListItemText
              primary={
                <Link
                  href={productUrl}
                  underline="hover"
                  aria-label={`View ${p.name || "product"} details`}
                  sx={[
                    {
                      display: "inline-block",
                      borderRadius: "8px",
                      padding: "4px 8px",
                      margin: "-4px -8px",
                      transition: "background-color 0.2s, opacity 0.2s",
                    },
                    (theme) =>
                      theme.applyStyles("light", {
                        "&:focus-visible": {
                          outline: "none",
                          backgroundColor: alpha(
                            theme.palette.primary.dark,
                            theme.palette.action.focusOpacity,
                          ),
                        },
                      }),
                    (theme) =>
                      theme.applyStyles("dark", {
                        "&:focus-visible": {
                          outline: "none",
                          backgroundColor: alpha(
                            theme.palette.primary.light,
                            theme.palette.action.focusOpacity,
                          ),
                        },
                      }),
                  ]}
                >
                  {p.name}
                </Link>
              }
              slotProps={{
                primary: {
                  title: p.name ?? undefined,
                  variant: "body2",
                },
                secondary: {
                  variant: "caption",
                  color: "text.secondary",
                  "aria-label": `Manufacturer part number: ${p.manufacturerPartNumber || "Not available"}`,
                },
              }}
              secondary={p.manufacturerPartNumber}
            />
          </ListItem>
        );
      })}
    </List>
  );
}
